import { AssessmentSubtype, AssessmentType, SurveyStatus, TechnologySelectDto } from '@@types/api.types';
import { Modal } from '@components/core/modal.component';
import { BlueLabel } from '@components/shared/blue-label.component';
import { ErrorMessage } from '@components/shared/error-message.component';
import { FormField } from '@components/shared/form-field.component';
import { TextWithLabel } from '@components/shared/text-with-label.component';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@components/ui/accordion';
import { Badge } from '@components/ui/badge';
import { Checkbox } from '@components/ui/checkbox';
import { MultiSelect } from '@components/ui/multi-select';
import { useSurveyNavigation } from '@hooks/surveys.hook';
import { errorToast } from '@hooks/toast.hook';
import { cn } from '@lib/utils';
import { useGetTechnologySurveys, useInnovatorCreateSurvey } from '@queries/survey.query';
import { type SurveyCreateFormErrors, surveyCreateSchema } from '@schemas/survey.schema';
import { FunctionComponent, useState } from 'react';

type StartRapidTRLModalProps = {
  isOpen: boolean;
  onClose: () => void;
  technology?: TechnologySelectDto;
};

/**
 * This component is a wrapper for the StartRapidTRLForm component.
 * It is used to handle the open/close state of the modal.
 *
 * By using a wrapper component, we can avoid using useEffect to update the state of the modal with the default values
 */
export const StartTRLModal: FunctionComponent<StartRapidTRLModalProps> = (props) => {
  const { isOpen, onClose, technology } = props;

  if (!isOpen || !technology) return null;

  return (
    <StartTRLModalContent
      onClose={onClose}
      technology={technology}
    />
  );
};

type StartTRLModalContentProps = {
  onClose: () => void;
  technology: TechnologySelectDto;
};

/**
 * This component is the form that allows the user to start a new TRL assessment.
 * It is used to select the uses and domains that the user wants to assess.
 * It also checks if there is an existing draft TRL assessment for the technology.
 * If there is, it will display an error message.
 * If there is no existing draft, it will allow the user to start a new assessment.
 */
const StartTRLModalContent: FunctionComponent<StartTRLModalContentProps> = (props) => {
  const { onClose, technology } = props;

  const { navigateToSurvey } = useSurveyNavigation();
  const { mutateAsync: createSurvey, isPending: isSurveyCreating } = useInnovatorCreateSurvey();

  const { data: draftSurveys = [] } = useGetTechnologySurveys({
    status: SurveyStatus.DRAFT,
    technologyId: technology.id,
  });

  const hasExistingDraft = draftSurveys.some(
    (survey) =>
      survey.technology?.id === technology.id &&
      survey.assessmentType === AssessmentType.TRL &&
      survey.assessmentSubtype !== AssessmentSubtype.MILITARY,
  );

  const initialSelectedUses = technology.uses.map((use) => use.useId);
  const initialSelectedDomains = technology.uses.reduce((acc, use) => {
    acc[use.useId] = use.domains.map((domain) => domain.id);
    return acc;
  }, {});

  const [selectedDomains, setSelectedDomains] = useState<Record<string, string[]>>(initialSelectedDomains);
  const [selectedUses, setSelectedUses] = useState<string[]>(initialSelectedUses);
  const [validationErrors, setValidationErrors] = useState<SurveyCreateFormErrors | undefined>();

  const handleDomainSelect = (useId: string, domainIds: string[]) => {
    setSelectedDomains((prev) => ({
      ...prev,
      [useId]: domainIds,
    }));

    // Clear validation errors when form state changes
    if (validationErrors) {
      setValidationErrors(undefined);
    }
  };

  const handleSubmit = async () => {
    setValidationErrors(undefined);

    // Check for existing draft first
    if (hasExistingDraft) {
      errorToast({
        description:
          'A draft TRL assessment already exists for this technology. Please complete or discard the existing assessment before starting a new one.',
      });
      return;
    }

    // Prepare form data with both UI state and API payload
    const formData = {
      assessmentType: AssessmentType.TRL,
      selectedDomains,
      selectedUses,
      technologyId: technology.id,
      uses: selectedUses.flatMap((useId) => {
        const domainIds = selectedDomains[useId] || [];
        return domainIds.map((domainId) => ({ domainId, useId }));
      }),
    };

    // Validate using Zod schema (includes both UI and API validation)
    const validationResult = surveyCreateSchema.safeParse(formData);

    if (!validationResult.success) {
      const firstError = validationResult.error.errors[0];
      errorToast({ description: firstError.message });
      setValidationErrors(validationResult.error.format());
      return;
    }

    const survey = await createSurvey(validationResult.data);
    onCloseOverride();
    navigateToSurvey(`/technology/${technology.id}/survey/${survey.id}`);
  };

  const handleUseCheckbox = (useId: string) => {
    setSelectedUses((prev) => {
      const isCurrentlySelected = prev.includes(useId);

      // Toggle logic
      const newSelectedUses = isCurrentlySelected ? prev.filter((id) => id !== useId) : [...prev, useId];

      // Ensure we have an entry for this use in selectedDomains to avoid undefined look-ups
      if (!isCurrentlySelected) {
        setSelectedDomains((prevDomains) => ({
          ...prevDomains,
          [useId]: prevDomains[useId] ?? [],
        }));
      }

      // Clear validation errors when form state changes
      if (validationErrors) {
        setValidationErrors(undefined);
      }

      return newSelectedUses;
    });
  };

  const onCloseOverride = () => {
    setValidationErrors(undefined);
    onClose();
  };

  return (
    <Modal
      centered
      footer={{
        rightButton: {
          loading: isSurveyCreating,
          onClick: handleSubmit,
          children: 'Start TRL',
        },
      }}
      header={{ title: 'Start TRL Assessment' }}
      isOpen
      onClose={onCloseOverride}
      width="620px"
    >
      <div className="w-full">
        <div className="mb-4">
          <BlueLabel text={technology.name} />
          <div className="text-sm text-gray-500">Select Uses and Domains to assess.</div>
        </div>

        <div className="overflow-y-auto space-y-3">
          {technology.uses.map((use, useIndex) => {
            const selectedDomainCount = selectedDomains[use.useId]?.length || 0;

            return (
              <div
                className={cn('rounded-xl border border-gray-200', {
                  'border-red-500': validationErrors?.selectedDomains?.[use.useId],
                })}
                key={use.useId}
              >
                <Accordion
                  type="single"
                  collapsible
                  className="w-full"
                >
                  <AccordionItem
                    value="domains"
                    className="border-0"
                  >
                    <AccordionTrigger className="py-3 px-4 hover:no-underline">
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-4">
                          <div onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedUses.includes(use.useId)}
                              className="size-6"
                              onCheckedChange={() => handleUseCheckbox(use.useId)}
                            />
                          </div>
                          <div>
                            <TextWithLabel
                              badge={
                                useIndex === 0 ? (
                                  <Badge
                                    variant="success"
                                    size="xs"
                                  >
                                    Primary Use
                                  </Badge>
                                ) : undefined
                              }
                              className="text-sm text-left"
                              label="Use"
                              title={use.name}
                            />
                          </div>
                        </div>
                        <span className="text-sm text-gray-500 pr-2">
                          {selectedDomainCount} Domain{selectedDomainCount !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pl-14 pr-4 pb-4">
                      <FormField>
                        <MultiSelect
                          disabled={!selectedUses.includes(use.useId)}
                          hasError={!!validationErrors?.selectedDomains?.[use.useId]}
                          key={`${use.useId}-${selectedUses.includes(use.useId)}`}
                          onValueChange={(domainIds) => handleDomainSelect(use.useId, domainIds)}
                          options={use.domains.map((domain) => ({
                            label: domain.name,
                            value: domain.id,
                          }))}
                          placeholder="Select domains..."
                          value={selectedDomains[use.useId] || []}
                        />
                        <ErrorMessage zodError={validationErrors?.selectedDomains?.[use.useId]} />
                      </FormField>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
};
